import React, { useState } from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../../../contexts/AuthContext';
import InternshipApplication from '../../student/InternshipApplication/InternshipApplicationNew';
import { PageHeader, PageTitle } from '../../../styles/components';
import internships from '../../../data/mock/internships';
import {
  InternshipContainer,
  FiltersContainer,
  FilterRow,
  FilterGroup,
  FilterLabel,
  FilterSelect,
  FilterInput,
  FilterActions,
  ClearButton,
  InternshipList,
  InternshipCard,
  InternshipCardHeader,
  CompanyInfo,
  CompanyLogo,
  CompanyDetails,
  InternshipDetails,
  LocationInfo,
  TagsContainer,
  InternshipTag,
  ApplyButton,
  ContentSection,
  ModalOverlay,
  ModalContent
} from '../../../styles/components/InternshipListing';

// Using mock data imported from data/mock/internships.js

const InternshipListing = () => {
  const { user } = useAuth();
  // eslint-disable-next-line no-unused-vars
  const [internshipsList, setInternshipsList] = useState(internships);
  const [searchTerm, setSearchTerm] = useState('');
  const [industryFilter, setIndustryFilter] = useState('');
  const [durationFilter, setDurationFilter] = useState('');
  const [paidFilter, setPaidFilter] = useState('');
  const [selectedInternship, setSelectedInternship] = useState(null);
  const [showApplicationForm, setShowApplicationForm] = useState(false);

  // Handle search and filters
  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredInternships = internshipsList.filter(internship => {
    const matchesSearch = 
      internship.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
      internship.company.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesIndustry = 
      industryFilter === '' || internship.industry === industryFilter;
    
    const matchesDuration = 
      durationFilter === '' || 
      (durationFilter === '3 months or less' && parseInt(internship.duration) <= 3) ||
      (durationFilter === '3-6 months' && parseInt(internship.duration) > 3 && parseInt(internship.duration) <= 6) ||
      (durationFilter === 'Over 6 months' && parseInt(internship.duration) > 6);
    
    const matchesPaid = 
      paidFilter === '' || 
      (paidFilter === 'Paid' && internship.paid) ||
      (paidFilter === 'Unpaid' && !internship.paid);
    
    return matchesSearch && matchesIndustry && matchesDuration && matchesPaid;
  });

  const handleInternshipClick = (internship) => {
    setSelectedInternship(internship);
    setShowApplicationForm(false); // Make sure application form is closed when viewing details
  };

  const closeInternshipDetails = () => {
    setSelectedInternship(null);
    setShowApplicationForm(false); // Also close application form when closing details
  };

  const handleApplyNow = (e) => {
    // Prevent event propagation to avoid closing the modal
    if (e) e.stopPropagation();
    
    // Check if user is a student
    if (!isStudent) {
      toast.error('You must be logged in as a student to apply for internships', {
        position: 'top-right',
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        style: {
          background: 'rgba(15, 23, 42, 0.9)',
          backdropFilter: 'blur(10px)',
          color: '#fff',
          border: '1px solid rgba(239, 68, 68, 0.5)',
          borderRadius: '8px'
        }
      });
      return;
    }
    
    // Explicitly set showApplicationForm to true to display the application form
    setShowApplicationForm(true);
    console.log('Apply Now clicked, showing application form:', selectedInternship.title);
  };

  const closeApplicationForm = () => {
    setShowApplicationForm(false);
  };

  const isStudent = user && (user.role === 'student' || user.role === 'proStudent');
  const isCompany = user && user.role === 'company';
  const isScadOffice = user && user.role === 'scadOffice';
  const isFaculty = user && user.role === 'faculty';
  const canApply = isStudent && !isCompany && !isScadOffice && !isFaculty;

  return (
    <InternshipContainer>
      <PageHeader>
        <PageTitle>Available Internships</PageTitle>
      </PageHeader>
      
      {/* Search and Filter Section */}
      <FiltersContainer>
        <FilterRow>
          <FilterGroup>
            <FilterLabel htmlFor="search">Search by job title or company</FilterLabel>
            <FilterInput
              type="text"
              id="search"
              placeholder="Search..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel htmlFor="industry">Filter by Industry</FilterLabel>
            <FilterSelect
              id="industry"
              value={industryFilter}
              onChange={(e) => setIndustryFilter(e.target.value)}
            >
              <option value="">All Industries</option>
              <option value="Software Development">Software Development</option>
              <option value="Financial Services">Financial Services</option>
              <option value="Environmental Technology">Environmental Technology</option>
              <option value="Healthcare Technology">Healthcare Technology</option>
              <option value="Digital Media">Digital Media</option>
              <option value="Technology Consulting">Technology Consulting</option>
              <option value="Marketing">Marketing</option>
            </FilterSelect>
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel htmlFor="duration">Filter by Duration</FilterLabel>
            <FilterSelect
              id="duration"
              value={durationFilter}
              onChange={(e) => setDurationFilter(e.target.value)}
            >
              <option value="">All Durations</option>
              <option value="3 months or less">3 months or less</option>
              <option value="3-6 months">3-6 months</option>
              <option value="Over 6 months">Over 6 months</option>
            </FilterSelect>
          </FilterGroup>
          
          <FilterGroup>
            <FilterLabel htmlFor="paid">Filter by Compensation</FilterLabel>
            <FilterSelect
              id="paid"
              value={paidFilter}
              onChange={(e) => setPaidFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="Paid">Paid</option>
              <option value="Unpaid">Unpaid</option>
            </FilterSelect>
          </FilterGroup>
        </FilterRow>
        
        <FilterActions>
          <ClearButton onClick={() => {
            setSearchTerm('');
            setIndustryFilter('');
            setDurationFilter('');
            setPaidFilter('');
          }}>
            Clear Filters
          </ClearButton>
        </FilterActions>
      </FiltersContainer>
      
      {/* Internship List */}
      <InternshipList>
        {filteredInternships.length === 0 ? (
          <InternshipCard>
            <p>No internships match your search criteria.</p>
          </InternshipCard>
        ) : (
          filteredInternships.map(internship => (
            <InternshipCard 
              key={internship.id} 
              onClick={() => handleInternshipClick(internship)}
            >
              <InternshipCardHeader>
                <CompanyInfo>
                  <CompanyLogo>
                    <img 
                      src={internship.logo || internship.companyLogo} 
                      alt={`${internship.company} logo`} 
                    />
                  </CompanyLogo>
                  <CompanyDetails>
                    <h3>{internship.title}</h3>
                    <p>{internship.company}</p>
                    <LocationInfo>
                      <span>{internship.location}</span>
                    </LocationInfo>
                  </CompanyDetails>
                </CompanyInfo>
                <InternshipDetails>
                  <TagsContainer>
                    <InternshipTag>{internship.industry}</InternshipTag>
                    <InternshipTag>{internship.duration}</InternshipTag>
                    <InternshipTag className={internship.paid ? 'paid' : ''}>
                      {internship.paid ? 'Paid' : 'Unpaid'}
                    </InternshipTag>
                  </TagsContainer>
                </InternshipDetails>
              </InternshipCardHeader>
            </InternshipCard>
          ))
        )}
      </InternshipList>
      
      {/* Internship Details Modal */}
      {selectedInternship && !showApplicationForm && (
        <ModalOverlay>
          <ModalContent>
            <InternshipCardHeader>
              <h2>{selectedInternship.title}</h2>
              <button 
                onClick={closeInternshipDetails}
                className="close-button"
              >
                &times;
              </button>
            </InternshipCardHeader>
            
            <CompanyInfo>
              <CompanyLogo>
                <img 
                  src={selectedInternship.logo || selectedInternship.companyLogo} 
                  alt={`${selectedInternship.company} logo`} 
                />
              </CompanyLogo>
              <CompanyDetails>
                <h3>{selectedInternship.company}</h3>
                <LocationInfo>
                  <span>{selectedInternship.location}</span>
                </LocationInfo>
              </CompanyDetails>
            </CompanyInfo>
            
            <ContentSection>
              <h4>Internship Details</h4>
              <InternshipDetails>
                <div>
                  <p><strong>Industry:</strong> {selectedInternship.industry}</p>
                  <p><strong>Duration:</strong> {selectedInternship.duration}</p>
                </div>
                <div>
                  <p><strong>Compensation:</strong> {selectedInternship.paid ? 'Paid' : 'Unpaid'}</p>
                  {selectedInternship.paid && (
                    <p><strong>Salary:</strong> {selectedInternship.salary}</p>
                  )}
                </div>
              </InternshipDetails>
            </ContentSection>
            
            <ContentSection>
              <h4>Job Description</h4>
              <p>{selectedInternship.description}</p>
            </ContentSection>
            
            <ContentSection>
              <h4>Required Skills</h4>
              <TagsContainer>
                {selectedInternship.skills ? (
                  selectedInternship.skills.map((skill, index) => (
                    <InternshipTag key={index}>
                      {skill}
                    </InternshipTag>
                  ))
                ) : selectedInternship.requirements ? (
                  <p>{selectedInternship.requirements}</p>
                ) : (
                  <p>No skills information available</p>
                )}
              </TagsContainer>
            </ContentSection>
            
            <FilterActions style={{
              display: 'flex',
              gap: '1.5rem',
              justifyContent: 'flex-end',
              marginTop: '1.5rem',
              alignItems: 'center' // Add this to ensure vertical alignment
            }}>
              {canApply && (
                <ApplyButton 
                  type="button" 
                  onClick={(e) => {
                    e.stopPropagation(); // Stop event propagation
                    handleApplyNow(e);
                  }}
                  style={{
                    width: '150px', // Fixed width instead of minWidth for consistency
                    padding: '0.75rem 1.5rem',
                    fontSize: '1rem',
                    fontWeight: '600',
                    display: 'inline-flex', // Changed to inline-flex for better alignment
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '45px',
                    margin: '0' // Ensure no default margins affect alignment
                  }}
                >
                  Apply Now
                </ApplyButton>
              )}
              <ClearButton 
                onClick={closeInternshipDetails}
                style={{
                  width: '150px', // Fixed width instead of minWidth for consistency
                  padding: '0.75rem 1.5rem',
                  background: 'rgba(15, 23, 42, 0.5)',
                  border: '1px solid rgba(99, 102, 241, 0.3)',
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontWeight: '600',
                  fontSize: '1rem',
                  display: 'inline-flex', // Changed to inline-flex for better alignment
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '45px',
                  margin: '0' // Ensure no default margins affect alignment
                }}
              >
                Close
              </ClearButton>
            </FilterActions>
          </ModalContent>
        </ModalOverlay>
      )}
      
      {/* Application Form Modal */}
      {selectedInternship && showApplicationForm && (
        <ModalOverlay>
          <ModalContent>
            <InternshipApplication 
              internship={selectedInternship} 
              onClose={() => {
                closeApplicationForm();
                closeInternshipDetails();
              }}
            />
          </ModalContent>
        </ModalOverlay>
      )}
    </InternshipContainer>
  );
};

export default InternshipListing;